/**
 * 根據 formConfig 的設定轉換 formData 為 API payload
 *
 * 支援的 config 設定:
 * - payload_type: 'array' | 'int_array' - 將值轉換為陣列格式
 *
 * @param {Object} formData - 表單資料
 * @param {Array} formConfig - 表單配置（可包含巢狀陣列）
 * @param {Object} options - 額外選項
 * @param {Object} options.originalData - 原始資料，用於比較是否有變更（編輯模式）
 * @param {boolean} options.skipUnchanged - 是否跳過未變更的欄位（編輯模式使用）
 * @returns {Object} 轉換後的 payload
 */
export const transformFormPayload = (formData, formConfig, options = {}) => {
    const { originalData = null, skipUnchanged = false } = options;
    const payload = {};

    // 扁平化 formConfig（處理巢狀陣列）
    const flatConfigs = formConfig.flatMap(config =>
        Array.isArray(config) ? config : [config]
    );

    flatConfigs.forEach(config => {
        if (!config || !config.field) return;

        const field = config.field;
        let value = formData[field];

        // readonly 欄位特殊處理
        if (config.type === 'readonly') {
            // 支援 payload_field：指定要從 formData 取哪個欄位的值
            // 例如：{ field: 'club_name', type: 'readonly', payload_field: 'club' }
            // 會使用 formData.club 的值，並以 'club' 為 key 加入 payload
            if (config.payload_field) {
                const payloadValue = formData[config.payload_field];
                if (payloadValue !== undefined && payloadValue !== '') {
                    payload[config.payload_field] = payloadValue;
                }
                return;
            }

            // 如果有設定 value，則使用該值
            if (config.value !== undefined && config.value !== '') {
                payload[field] = config.value;
            }
            return;
        }

        // 編輯模式：跳過未變更的欄位
        if (skipUnchanged && originalData) {
            const originalValue = originalData[field];
            if (JSON.stringify(value) === JSON.stringify(originalValue)) {
                return;
            }
        }

        // 根據 payload_type 轉換值
        value = transformValueByPayloadType(value, config.payload_type);

        payload[field] = value;
    });

    return payload;
};

/**
 * 根據 payload_type 轉換單一值
 *
 * @param {any} value - 原始值
 * @param {string} payloadType - 'array' | 'int_array' | undefined
 * @returns {any} 轉換後的值
 */
export const transformValueByPayloadType = (value, payloadType) => {
    if (!payloadType) {
        // 如果是 object array，轉換為 id array（multi-select 的情況）
        if (
            Array.isArray(value) &&
            value.length > 0 &&
            typeof value[0] === 'object' &&
            value[0] !== null &&
            'id' in value[0]
        ) {
            return value.map(item => item.id);
        }
        return value;
    }

    switch (payloadType) {
        case 'array':
            // 將單一值包裝成陣列
            if (Array.isArray(value)) {
                // 如果已經是陣列，確保 object array 轉為 id array
                if (
                    value.length > 0 &&
                    typeof value[0] === 'object' &&
                    value[0] !== null &&
                    'id' in value[0]
                ) {
                    return value.map(item => item.id);
                }
                return value;
            }
            if (value === '' || value === null || value === undefined) {
                return [];
            }
            return [value];

        case 'int_array': {
            // 將值轉換為整數陣列
            if (Array.isArray(value)) {
                return value
                    .map(item => {
                        if (
                            typeof item === 'object' &&
                            item !== null &&
                            'id' in item
                        ) {
                            return Number(item.id);
                        }
                        const num = Number(item);
                        return Number.isInteger(num) ? num : null;
                    })
                    .filter(item => item !== null);
            }
            if (value === '' || value === null || value === undefined) {
                return [];
            }
            const num = Number(value);
            return Number.isInteger(num) ? [num] : [];
        }

        default:
            return value;
    }
};

/**
 * 從 formConfig 中取得特定欄位的設定
 *
 * @param {Array} formConfig - 表單配置
 * @param {string} fieldName - 欄位名稱
 * @returns {Object|null} 欄位設定
 */
export const getFieldConfig = (formConfig, fieldName) => {
    for (const config of formConfig) {
        if (Array.isArray(config)) {
            const found = config.find(c => c.field === fieldName);
            if (found) return found;
        } else if (config && config.field === fieldName) {
            return config;
        }
    }
    return null;
};

/**
 * 根據 payload_type 將 API 回傳的值轉換為表單可用的格式
 * 這是 transformValueByPayloadType 的反向操作
 *
 * 例如：
 * - payload_type: 'int_array' + type: 'select' => [5] -> 5 (array 取第一個元素)
 * - payload_type: 'array' + type: 'select' => ['value'] -> 'value'
 *
 * @param {any} value - API 回傳的值
 * @param {Object} config - 欄位設定 { type, payload_type }
 * @returns {any} 轉換後的值
 */
export const transformValueForForm = (value, config) => {
    if (!config) return value;

    const { type, payload_type } = config;

    // 如果沒有 payload_type，不需要轉換
    if (!payload_type) return value;

    // 如果是單選 UI 但 payload 是 array 格式
    if (
        type === 'select' &&
        (payload_type === 'array' || payload_type === 'int_array')
    ) {
        if (Array.isArray(value) && value.length > 0) {
            // 取陣列的第一個元素作為單選的值
            return value[0];
        } else if (Array.isArray(value) && value.length === 0) {
            // 空陣列返回空字串
            return '';
        }
    }

    return value;
};

/**
 * 根據 formConfig 將 API 回傳的資料轉換為表單可用的格式
 *
 * @param {Object} apiData - API 回傳的原始資料
 * @param {Array} formConfig - 表單配置
 * @returns {Object} 轉換後的表單資料
 */
export const transformApiDataForForm = (apiData, formConfig) => {
    const formData = {};

    // 扁平化 formConfig（處理巢狀陣列）
    const flatConfigs = formConfig.flatMap(config =>
        Array.isArray(config) ? config : [config]
    );

    flatConfigs.forEach(config => {
        if (!config || !config.field) return;

        const field = config.field;
        const apiValue = apiData[field];

        // 根據 payload_type 轉換值
        formData[field] = transformValueForForm(apiValue, config);
    });

    return formData;
};
